'use client';

import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { AnimatedNumber } from "./AnimatedNumber";
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  AreaChart, 
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';

interface UsageLevel {
  name: string;
  timePerDay: number;
  label: string;
}

const usageLevels: UsageLevel[] = [
  { name: "beginner", timePerDay: 30, label: "Beginner: 30 min per day" },
  { name: "intermediate", timePerDay: 45, label: "Intermediate: 45 min per day" },
  { name: "expert", timePerDay: 120, label: "Expert: 2 hours per day" },
];

const WORKING_DAYS_PER_YEAR = 250;

export function ROICalculator() {
  const [employees, setEmployees] = useState<number>(10);
  const [ratePerHour, setRatePerHour] = useState<number>(400);
  const [selectedLevel, setSelectedLevel] = useState<UsageLevel["name"]>("intermediate");
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '10%']);

  const generateChartData = () => {
    const days = [0, 50, 100, 150, 200, 250]; // Key points in the year
    const beginnerLevel = usageLevels[0]?.timePerDay ?? 30;
    const intermediateLevel = usageLevels[1]?.timePerDay ?? 45;
    const expertLevel = usageLevels[2]?.timePerDay ?? 120;
    
    return days.map(day => {
      const beginnerSavings = Math.round((beginnerLevel / 60) * ratePerHour * employees * day);
      const intermediateSavings = Math.round((intermediateLevel / 60) * ratePerHour * employees * day);
      const expertSavings = Math.round((expertLevel / 60) * ratePerHour * employees * day);
      
      return {
        day,
        beginner: beginnerSavings,
        intermediate: intermediateSavings,
        expert: expertSavings
      };
    });
  };

  const handleEmployeesChange = (value: number[]) => {
    if (value[0] !== undefined) {
      setEmployees(value[0]);
    }
  };

  const handleRateChange = (value: number[]) => {
    if (value[0] !== undefined) {
      setRatePerHour(value[0]);
    }
  };

  const annualSavings = Math.round(
    ((usageLevels.find(level => level.name === selectedLevel)?.timePerDay ?? 30) / 60) 
    * ratePerHour 
    * employees 
    * WORKING_DAYS_PER_YEAR
  );

  return (
    <section className="py-20 md:py-28 bg-gray-50">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex flex-col items-center gap-8 md:gap-12 max-w-7xl mx-auto">
          <motion.div 
            className="w-full max-w-2xl text-center"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h3 className="text-sm font-heading font-semibold text-primary uppercase tracking-wider mb-3">
              Return on Investment
            </h3>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 tracking-tight leading-tight mb-6">
              Calculate your savings
            </h2>
            <p className="text-lg font-sans text-gray-600 leading-relaxed mb-12">
              See how much time and money you could save with Whisperit
            </p>
          </motion.div>

          <motion.div 
            className="w-full max-w-7xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Card className="p-6 md:p-8 bg-white shadow-sm">
              <div className="flex items-center justify-center mb-12">
                <h3 className="text-3xl font-heading font-semibold text-gray-900">Your savings over one year</h3>
              </div>
              <div className="space-y-6">
                {/* Calculator Controls */}
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 justify-center max-w-2xl mx-auto"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <div className="space-y-3">
                    <div className="text-base font-medium flex justify-between">
                      <span>Number of lawyers</span>
                      <span className="text-primary">{employees}</span>
                    </div>
                    <Slider
                      value={[employees]}
                      onValueChange={handleEmployeesChange}
                      min={1}
                      max={500}
                      step={1}
                      className="py-4"
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="text-base font-medium flex justify-between">
                      <span>Rate per hour</span>
                      <span className="text-primary">CHF {ratePerHour}</span>
                    </div>
                    <Slider
                      value={[ratePerHour]}
                      onValueChange={handleRateChange}
                      min={0}
                      max={600}
                      step={10}
                      className="py-4"
                    />
                  </div>
                </motion.div>

                <div className="space-y-12">
                  {/* Header with Title and Usage Levels */}
                  <motion.div 
                    className="text-center space-y-6"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      {usageLevels.map((level) => (
                        <div 
                          key={level.name} 
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer ${
                            selectedLevel === level.name 
                              ? 'bg-gray-100 shadow-sm' 
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedLevel(level.name)}
                        >
                          <div 
                            className="w-2.5 h-2.5 rounded-full"
                            style={{
                              backgroundColor: level.name === 'beginner' ? '#E7EFF9' : 
                                            level.name === 'intermediate' ? '#5B8BD4' : '#143C6C',
                              opacity: selectedLevel === level.name ? 1 : 0.4
                            }}
                          />
                          <span className={`text-sm ${
                            selectedLevel === level.name 
                              ? 'text-gray-900 font-medium' 
                              : 'text-gray-600'
                          }`}>
                            {level.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Graph and Results Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
                    {/* Graph Section */}
                    <motion.div 
                      className="lg:col-span-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.5 }}
                    >
                      <div className="h-[400px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={generateChartData()}
                            margin={{
                              top: 10,
                              right: 10,
                              left: 40,
                              bottom: 20,
                            }}
                          >
                            <defs>
                              <linearGradient id="beginnerGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#E7EFF9" stopOpacity={0.7} />
                                <stop offset="95%" stopColor="#E7EFF9" stopOpacity={0.1} />
                              </linearGradient>
                              <linearGradient id="intermediateGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#5B8BD4" stopOpacity={0.7} />
                                <stop offset="95%" stopColor="#5B8BD4" stopOpacity={0.1} />
                              </linearGradient>
                              <linearGradient id="expertGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#143C6C" stopOpacity={0.7} />
                                <stop offset="95%" stopColor="#143C6C" stopOpacity={0.1} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid 
                              strokeDasharray="3 3" 
                              strokeOpacity={0.1} 
                              vertical={false}
                            />
                            <XAxis 
                              dataKey="day"
                              axisLine={false}
                              tickLine={false}
                              tickMargin={10}
                              tick={{ fill: '#6B7280', fontSize: 12 }}
                            />
                            <YAxis 
                              tickFormatter={(value) => `${(value / 1000).toFixed(0)}k.-`}
                              axisLine={false}
                              tickLine={false}
                              tickMargin={10}
                              tick={{ fill: '#6B7280', fontSize: 12 }}
                            />
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                                padding: '12px'
                              }}
                              formatter={(value) => [`CHF ${value.toLocaleString()}`, 'Savings']}
                              labelFormatter={(label) => `Day ${label}`}
                            />
                            <Area
                              type="monotone"
                              dataKey="beginner"
                              stroke={selectedLevel === "beginner" ? "#E7EFF9" : "transparent"}
                              fill="url(#beginnerGradient)"
                              fillOpacity={selectedLevel === "beginner" ? 0.9 : 0.2}
                              strokeWidth={1}
                              name="Beginner: 30 min per day"
                            />
                            <Area
                              type="monotone"
                              dataKey="intermediate"
                              stroke={selectedLevel === "intermediate" ? "#5B8BD4" : "transparent"}
                              fill="url(#intermediateGradient)"
                              fillOpacity={selectedLevel === "intermediate" ? 0.9 : 0.2}
                              strokeWidth={1}
                              name="Intermediate: 45 min per day"
                            />
                            <Area
                              type="monotone"
                              dataKey="expert"
                              stroke={selectedLevel === "expert" ? "#143C6C" : "transparent"}
                              fill="url(#expertGradient)"
                              fillOpacity={selectedLevel === "expert" ? 0.9 : 0.2}
                              strokeWidth={1}
                              name="Expert: 2 hours per day"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </motion.div>

                    {/* Results Display */}
                    <motion.div 
                      className="lg:col-span-2 flex items-center"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                    >
                      <div className="w-full bg-gray-50 rounded-xl p-6 md:p-8">
                        <div className="flex flex-col justify-center text-left space-y-6">
                          <p className="text-lg text-gray-600">
                            With {employees} lawyer{employees !== 1 ? 's' : ''} billing CHF {ratePerHour}/hour and using Whisperit at {' '}
                            {usageLevels.find(level => level.name === selectedLevel)?.label.toLowerCase().split(':')[0] ?? 'beginner'}{' '}
                            level, your firm can reclaim up to CHF
                          </p>
                          <div>
                            <div className="text-[#143C6C] text-5xl md:text-6xl lg:text-6xl font-heading font-bold tracking-tight mb-2 overflow-hidden">
                              <div className="overflow-x-auto whitespace-nowrap max-w-full pb-1">
                                <AnimatedNumber
                                  value={annualSavings}
                                  duration={1500}
                                  className="text-5xl md:text-6xl lg:text-6xl xl:text-6xl 2xl:text-7xl"
                                />
                              </div>
                            </div>
                            <p className="text-xl text-gray-600">per year</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
} 